import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2, Clock, Flame, FileText, AlertTriangle, TrendingUp, DollarSign, Users, ArrowRight } from "lucide-react";
import { getOwnerDashboard, completeTask } from "@/lib/crm.functions";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: Today,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
});

const fmtUsd = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Today() {
  const load = useServerFn(getOwnerDashboard);
  const done = useServerFn(completeTask);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["crm", "today"], queryFn: () => load(), staleTime: 30_000 });
  const complete = useMutation({
    mutationFn: (id: string) => done({ data: { id, done: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });

  if (isLoading) return <div className="grid place-items-center py-24"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!data) return null;

  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <header>
        <p className="text-amber-300 text-[11px] tracking-[0.25em] uppercase font-semibold">Today · {now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
        <h1 className="font-serif text-3xl lg:text-4xl text-white mt-1">{greeting}, Michael.</h1>
        <p className="text-sm text-zinc-400 mt-2">Here's what needs your attention today.</p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric icon={Users} label="New leads (7d)" value={String(data.week.newLeads)} />
        <Metric icon={FileText} label="Proposals sent (7d)" value={String(data.week.proposalsSent)} />
        <Metric icon={CheckCircle2} label="Jobs won (7d)" value={String(data.week.jobsWon)} />
        <Metric icon={DollarSign} label="Revenue (7d)" value={fmtUsd(data.week.revenue)} />
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Today's tasks" icon={Clock} href="/admin/tasks" count={data.tasksToday.length}>
          {data.tasksToday.length === 0 ? (
            <Empty text="Inbox zero. No tasks due today." />
          ) : (
            <ul className="divide-y divide-white/5">
              {data.tasksToday.map((t: any) => (
                <li key={t.id} className="flex items-start gap-3 py-3">
                  <button
                    onClick={() => complete.mutate(t.id)}
                    className="mt-0.5 size-5 rounded-full border border-white/20 grid place-items-center hover:bg-amber-300/20 hover:border-amber-300 transition"
                    aria-label="Complete task"
                  >
                    <CheckCircle2 className="size-3 text-transparent hover:text-amber-300" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{t.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {t.crm_contacts?.name ? <>{t.crm_contacts.name} · </> : null}
                      {t.due_at ? `Due ${fmtDateTime(t.due_at)}` : "No due date"}
                      {t.priority === "high" ? <span className="ml-2 text-amber-300">High</span> : null}
                    </p>
                  </div>
                  {t.contact_id && (
                    <Link to="/admin/contacts/$id" params={{ id: t.contact_id }} className="text-[11px] text-amber-300 hover:underline shrink-0">Open</Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Hot leads" icon={Flame} href="/admin/pipeline" count={data.hotLeads.length}>
          {data.hotLeads.length === 0 ? (
            <Empty text="No active leads in the pipeline." />
          ) : (
            <ul className="divide-y divide-white/5">
              {data.hotLeads.map((c: any) => (
                <li key={c.id} className="py-3">
                  <Link to="/admin/contacts/$id" params={{ id: c.id }} className="flex items-center justify-between gap-3 group">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate group-hover:text-amber-200">{c.name ?? c.email ?? "Unnamed"}</p>
                      <p className="text-[11px] text-zinc-500 truncate">
                        <StageBadge stage={c.pipeline_stage} />
                        {c.city ? <span className="ml-2">{c.city}</span> : null}
                        <span className="ml-2">Last active {fmtDate(c.last_activity_at)}</span>
                      </p>
                    </div>
                    <ArrowRight className="size-4 text-zinc-500 group-hover:text-amber-300 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Proposals awaiting response" icon={FileText} href="/admin/proposals" count={data.pendingProposals.length}>
          {data.pendingProposals.length === 0 ? (
            <Empty text="Nothing waiting on customer sign-off." />
          ) : (
            <ul className="divide-y divide-white/5">
              {data.pendingProposals.map((p: any) => (
                <li key={p.id} className="py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{p.customer_name ?? "Customer"} · {p.project_type ?? "Proposal"}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {fmtUsd(Number(p.total ?? 0))} · Sent {fmtDate(p.sent_at)}
                        {p.first_viewed_at ? <> · Viewed {fmtDate(p.first_viewed_at)}</> : <> · <span className="text-amber-300">Not yet viewed</span></>}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400 shrink-0">{p.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Overdue invoices" icon={AlertTriangle} href="/admin/accounting" count={data.overdueInvoices.length}>
          {data.overdueInvoices.length === 0 ? (
            <Empty text="A/R is clean." />
          ) : (
            <ul className="divide-y divide-white/5">
              {data.overdueInvoices.map((i: any) => (
                <li key={i.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{i.invoice_number} · {i.customer_name ?? ""}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">Due {fmtDate(i.due_date)} · {fmtUsd(Number(i.balance_due ?? 0))} open</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-red-300 shrink-0">Overdue</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
        <Icon className="size-3.5 text-amber-300" />
        {label}
      </div>
      <p className="mt-2 font-serif text-2xl text-white">{value}</p>
    </div>
  );
}

function Card({ title, icon: Icon, href, count, children }: { title: string; icon: any; href: string; count?: number; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-amber-300" />
          <h2 className="font-serif text-lg text-white">{title}</h2>
          {typeof count === "number" && <span className="text-[11px] text-zinc-500">({count})</span>}
        </div>
        <Link to={href as any} className="text-[11px] text-amber-300 hover:underline">View all →</Link>
      </div>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-zinc-500 py-6 text-center">{text}</p>;
}

function StageBadge({ stage }: { stage: string }) {
  const map: Record<string, string> = {
    new: "text-sky-300", contacted: "text-amber-300", estimate: "text-violet-300",
    proposal: "text-emerald-300", won: "text-emerald-400", lost: "text-zinc-500",
  };
  return <span className={`uppercase text-[10px] tracking-widest ${map[stage] ?? "text-zinc-400"}`}>{stage}</span>;
}